import React, { useCallback } from 'react';
import { action } from 'mobx';
import { observer } from 'mobx-react';

import { Modal } from 'ui/base/modal';
import { BooleanStore } from 'ui/base/input';
import { ButtonClickEvent } from 'ui/base/types';
import { Theme, ThemedStyles, useStyles } from 'ui/base/theme';
import { BackButton } from 'ui/base/button/back_button';
import { StandardPage } from 'ui/base/page/standard_page';

import { LinkButton } from 'ui/public/button';
import { CheckboxInput } from 'ui/standard/input/checkbox_input';

import {
  Action,
  getFullAction,
  getShortAction,
  getMicroAction,
} from 'assets/enums/actions/actions';

import { ActionStore } from './action_store';
import baseStyles from './action_input.module.css';
import ActionInputMessages from './action_input_messages';

// Style declarations and themes
type ActionInputStyles = {
  box: string;
  placeholder: string;
  modalContainer: string;
  header: string;
  commit: string;
  toggleAll: string;
  inputRow: string;
  inputLabel: string;
  inputLabelSymbol: string;
  inputLabelText: string;
};

const themedStyles: ThemedStyles<ActionInputStyles> = {
  [Theme.DAYLIGHT]: {
    box: baseStyles.daylightBox,
    placeholder: baseStyles.daylightPlaceholder,
    modalContainer: baseStyles.modalContainer,
    header: baseStyles.header,
    commit: baseStyles.daylightCommit,
    toggleAll: baseStyles.daylightToggleAll,
    inputRow: baseStyles.inputRow,
    inputLabel: baseStyles.inputLabel,
    inputLabelSymbol: baseStyles.inputLabelSymbol,
    inputLabelText: baseStyles.inputLabelText,
  },
  [Theme.MIDNIGHT]: {
    box: baseStyles.midnightBox,
    placeholder: baseStyles.midnightPlaceholder,
    modalContainer: baseStyles.modalContainer,
    header: baseStyles.header,
    commit: baseStyles.midnightCommit,
    toggleAll: baseStyles.midnightToggleAll,
    inputRow: baseStyles.inputRow,
    inputLabel: baseStyles.inputLabel,
    inputLabelSymbol: baseStyles.inputLabelSymbol,
    inputLabelText: baseStyles.inputLabelText,
  },
};

// A component to render a row in the modal
type ActionInputRowProps = {
  symbol: string;
  text: string;
  store: BooleanStore;
  styles: ActionInputStyles;
};

const ActionInputRow = observer(({ symbol, text, store, styles }: ActionInputRowProps) => {
  const toggleStore = useCallback(action(() => {
    store.value = !store.value;
  }), [store]);

  return (
    <div className={styles.inputRow}>
      <div className={styles.inputLabel} onClick={toggleStore}>
        <div className={styles.inputLabelSymbol}>
          {symbol}
        </div>
        <div className={styles.inputLabelText}>
          {text}
        </div>
      </div>
      <CheckboxInput store={store}/>
    </div>
  );
});

// The component that handles the actual modal
type ActionInputModalProps = {
  store: ActionStore;
};

const ActionInputModal = observer(({ store }: ActionInputModalProps) => {
  const styles = useStyles(themedStyles);

  const onCommit = React.useCallback(action((event: ButtonClickEvent) => {
    event.stopPropagation();
    store.commitWorkspace();
    store.modal.hideModal();
  }), [store]);

  // Note: toggleAll functionality can be abstracted away into a component that
  // takes a list of boolean stores as argument. We can do that once a uniform
  // 'styling' for this feature has been decided.
  const onToggleAll = React.useCallback(action((event: ButtonClickEvent) => {
    event.stopPropagation();
    const toCheckAll = !store.workspaceHasChecked;
    Object.values(store.workspace).forEach((booleanStore) => {
      booleanStore.value = toCheckAll;
    });
  }), [store]);

  return (
    <div className={styles.modalContainer}>
      <StandardPage>
        <div className={styles.header}>
          <BackButton onClick={store.modal.hideModal}/>
          <button type='button' className={styles.commit} onClick={onCommit}>
            {ActionInputMessages.ok()}
          </button>
        </div>
        <div className={styles.inputRow}>
          <LinkButton type='button' className={styles.toggleAll} onClick={onToggleAll}>
            {!store.workspaceHasChecked ? ActionInputMessages.checkAll() : ActionInputMessages.uncheckAll()}
          </LinkButton>
        </div>
        <ActionInputRow
          styles={styles}
          symbol={getMicroAction(Action.ALLOWED)}
          text={getShortAction(Action.ALLOWED)}
          store={store.workspace[Action.ALLOWED]}
        />
        <ActionInputRow
          styles={styles}
          symbol={getMicroAction(Action.BLOCKED)}
          text={getShortAction(Action.BLOCKED)}
          store={store.workspace[Action.BLOCKED]}
        />
      </StandardPage>
    </div>
  );
});

// The component that handles the actual modal
type ActionInputBoxProps = {
  placeholder: string;
  store: ActionStore;
};

const ActionInputBox = observer(({ placeholder, store }: ActionInputBoxProps) => {
  const styles = useStyles(themedStyles);
  const onShowModal = React.useCallback(action(() => {
    store.reloadWorkspace();
    store.modal.showModal();
  }), [store]);

  if (store.arrayValue.length > 0) {
    return (
      <button type='button' className={styles.box} onClick={onShowModal}>
        {store.arrayValue.map((act) => getFullAction(act)).join(', ')}
      </button>
    );
  } else {
    return (
      <button type='button' className={styles.box} onClick={onShowModal}>
        <span className={styles.placeholder}>{placeholder}</span>
      </button>
    );
  }
});

export type ActionInputProps = {
  placeholder: string;
  store: ActionStore;
};

export const ActionInput = observer(({ placeholder, store }: ActionInputProps) => {
  return (
    <>
      <Modal store={store.modal}>
        <ActionInputModal store={store}/>
      </Modal>
      <ActionInputBox placeholder={placeholder} store={store}/>
    </>
  );
});
